import React, { useState, useEffect, useRef } from 'react';
import styles from './TerminalWindow.module.css';

const TerminalWindow = () => {
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState('');
  const [isBooting, setIsBooting] = useState(true);
  const [bootIndex, setBootIndex] = useState(0);
  const scrollRef = useRef(null);

  const banner = `
  _____              _            _      _ _ 
 |  __ _            | |          | |    (_) |
 | |__) |_ _ _ __ __| | __ _ _ __| |     _| |
 |  ___/ _\` | '__/ _\` |/ _\` | '__| |    | | |
 | |  | (_| | | | (_| | (_| | |  | |____| | |
 |_|   \\__,_|_|  \\__,_|\\__,_|_|  |______|_|_|
                                             
 AGENTIC-OS v2.0.4 - KERNEL ACTIVE
 TYPE 'help' TO DISCOVER MY CAPABILITIES
  `;

  const bootSequence = [
    { type: 'system', content: '[OK] Initializing Paras_Jain_Core.sys' },
    { type: 'system', content: '[OK] Loading Agentic AI Framework...' },
    { type: 'system', content: '[OK] Java Spring Boot Environment [READY]' },
    { type: 'system', content: '[OK] Connection Established: 127.0.0.1' },
    { type: 'system', content: '[OK] Neural Engine Online' },
    { type: 'system', content: '----------------------------------------' },
    { type: 'banner', content: banner },
  ];

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, isBooting]);

  // Initial Boot Sequence Animation
  useEffect(() => {
    if (bootIndex < bootSequence.length) {
      const timer = setTimeout(() => {
        setHistory(prev => [...prev, bootSequence[bootIndex]]);
        setBootIndex(prev => prev + 1);
      }, bootIndex === bootSequence.length - 1 ? 1000 : 250);
      return () => clearTimeout(timer);
    } else {
      setIsBooting(false);
    }
  }, [bootIndex]);

  const processCommand = (cmd) => {
    const cleanCmd = cmd.toLowerCase().trim();
    let response = [];

    switch (cleanCmd) {
      case 'help':
        response = [
          { type: 'info', content: 'Available Commands:' },
          { type: 'info', content: ' - whois    : Brief professional summary' },
          { type: 'info', content: ' - skills   : My technical arsenal' },
          { type: 'info', content: ' - projects : Key portfolio highlights' },
          { type: 'info', content: ' - agents   : AI Agent fleet status' },
          { type: 'info', content: ' - clear    : Wipe terminal terminal' },
        ];
        break;
      case 'whois':
        response = [
          { type: 'data', content: 'USER: Paras Jain' },
          { type: 'data', content: 'ROLE: Full Stack Software Engineer & AI Architect' },
          { type: 'data', content: 'BIO : Specializing in intelligent microservices using Java/Spring Boot' },
          { type: 'data', content: '      and building autonomous Multi-Agent Systems.' }
        ];
        break;
      case 'skills':
        response = [
          { type: 'data', content: 'BACKEND : Java (Spring Boot), Python, SQL/NoSQL' },
          { type: 'data', content: 'FRONTEND: React, Next.js, CSS/SCSS' },
          { type: 'data', content: 'AI      : Agentic Workflows, LangChain, CrewAI, AutoGen' },
          { type: 'data', content: 'DEVOPS  : Docker, Kubernetes, CI/CD, AWS' }
        ];
        break;
      case 'projects':
        response = [
          { type: 'info', content: 'TOP PROJECTS:' },
          { type: 'data', content: '1. Agentic Workflow Engine - Multi-agent orchestration' },
          { type: 'data', content: '2. Smart Microservices Hub - Scalable Spring Boot mesh' },
          { type: 'data', content: '3. Neural Chat Interface - Real-time AI collaboration' },
        ];
        break;
        case 'agents':
          response = [
            { type: 'info', content: 'AI FLEET STATUS:' },
            { type: 'system', content: '[OK] LogicEngine_01 - STABLE' },
            { type: 'system', content: '[OK] MemoryManager_04 - SYNCED' },
            { type: 'system', content: '[OK] TaskExecutor_09 - EXECUTING...' },
            { type: 'system', content: '[OK] ResponseAgent_12 - READY' },
          ];
          break;
      case 'clear':
        setHistory([]);
        return;
      default:
        response = [{ type: 'error', content: `Command not found: ${cleanCmd}. Type 'help' for options.` }];
    }

    setHistory(prev => [...prev, { type: 'prompt', content: `guest@paras-jain:~$ ${cmd}` }, ...response]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && input.trim()) {
      processCommand(input);
      setInput('');
    }
  };

  const QuickCommand = ({ cmd }) => (
    <button className={styles.quickCmd} onClick={() => processCommand(cmd)}>
      [{cmd}]
    </button>
  );

  return (
    <div className={styles.terminalContainer}>
      {/* ── CRT Scanline & Grain Effects Overlay ── */}
      <div className={styles.terminalEffects}></div>
      
      <div className={styles.terminalHeader}>
        <div className={styles.buttons}>
          <span className={styles.btnRed}></span>
          <span className={styles.btnYellow}></span>
          <span className={styles.btnGreen}></span>
        </div>
        <div className={styles.title}>guest@paras-jain [AGENTIC OS]</div>
        <div className={styles.stats}>CPU: 2% MEM: 14%</div>
      </div>

      <div className={styles.terminalBody} ref={scrollRef}>
        {history.map((line, index) => (
          <div key={index} className={`${styles.terminalLine} ${styles[line.type]}`}>
            {line.type === 'banner' ? <pre>{line.content}</pre> : line.content}
          </div>
        ))}
        
        {!isBooting && (
          <div className={styles.inputArea}>
            <span className={styles.prompt}>guest@paras-jain:~$</span>
            <div className={styles.inputWrapper}>
              <span className={styles.inputText}>{input}</span>
              <span className={styles.cursor}>█</span>
              <input 
                type="text" 
                className={styles.terminalInput}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                spellCheck="false"
                autoFocus
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Quick Interaction Buttons for Recruiters ── */}
      {!isBooting && (
        <div className={styles.quickActions}>
          <QuickCommand cmd="whois" />
          <QuickCommand cmd="skills" />
          <QuickCommand cmd="projects" />
          <QuickCommand cmd="agents" />
          <QuickCommand cmd="help" />
        </div>
      )}
    </div>
  );
};

export default TerminalWindow;
